/**
 * Orquestador de la sesion de checkout (plan 7.3.3).
 *
 * Responsabilidades de esta pantalla, y de ninguna otra:
 *  - abrir el canal con el SDK y emitir `apipay:ready` al montar;
 *  - re-verificar el origen embebedor contra `checkout.allowed_origins` (7.3.6);
 *  - aplicar el tema del tenant tras validarlo (7.3.5);
 *  - emitir `apipay:resize` con ResizeObserver, con debounce por rAF;
 *  - emitir EXACTAMENTE UNA VEZ el desenlace (`success` / `error` / `cancel`);
 *  - elegir que componente se renderiza segun `UiState`.
 *
 * Las estrategias de pasarela y los componentes de presentacion viven en
 * `src/gateways/` y `src/components/`: esta pantalla solo los compone.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import { createBridge } from '../bridge/postMessage';
import type { MessageTarget, WidgetBridge } from '../bridge/postMessage';
import { HandoffScreen } from '../components/HandoffScreen';
import { MethodSelector } from '../components/MethodSelector';
import { PaymentSummary } from '../components/PaymentSummary';
import { PromoCarousel } from '../components/PromoCarousel';
import { SessionErrorScreen } from '../components/SessionErrorScreen';
import { formatAmount } from '../money/formatAmount';
import { ProcessingScreen } from '../components/ProcessingScreen';
import { StatusScreen } from '../components/StatusScreen';
import { navegarALaTienda } from '../components/merchantReturn';
import { claseDeColumnaPrincipal, claseDeRejilla, pintaResumen } from './layoutPagina';
import { GatewayFlow } from '../gateways/GatewayFlow';
import type { Locale } from '../i18n';
import { t } from '../i18n';
import {
  assertEmbeddedFromAllowedOrigin,
  currentFrameContext,
  detectEmbedderOrigin,
} from '../security/frameGuard';
import type { FrameContext } from '../security/frameGuard';
import { bundledPromoSource } from '../promos/promos';
import { applyTenantTheme } from '../theme/applyTenantTheme';
import type { PaymentIntent, PaymentIntentCheckout } from '../types/api';
import { problemMessageKey, publicKeyFromConfig, sessionIdFromConfig } from '../types/api';
import {
  recallMerchantReturnUrl,
  recallPendingInscription,
  rememberMerchantReturnUrl,
} from './hostedSession';
import { intentIdFromClientSecret } from './parseFragment';
import type { UiState } from './uiState';
import type { CheckoutSession } from './useCheckoutSession';
import { useCheckoutSession } from './useCheckoutSession';
import { useAvisoYCierreDeVentana } from './ventanaDelComercio';

export interface SessionPageProps {
  clientSecret: string;
  publicKey: string;
  nonce: string;
  locale: Locale;
  apiBaseUrl: string;
  /** Solo para tests: sustituye la deteccion real del contexto de embebido. */
  frameContext?: FrameContext | undefined;
  /** Solo para tests: sustituye `window.parent` como destino del canal. */
  messageTarget?: MessageTarget | undefined;
}

/** Altura maxima que el SDK acepta; evita emitir resizes inutiles. */
const MAX_HEIGHT_PX = 2000;

/** Contenedor raiz; usa las mismas CSS variables validadas que los componentes. */
// `max-w-md` centrado y con aire: embebido ocupa el ancho del contenedor del comercio, y en modo
// pagina queda como una columna de checkout en vez de un bloque pegado al borde izquierdo.
const ROOT_CLASS =
  'mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-6 text-[var(--apipay-color-text,#0f172a)] sm:py-10';

/*
 * MODO PAGINA. El checkout deja de ser un widget dentro de otra pagina y ES el documento, asi que
 * tiene su propio fondo (clase .apipay-page de styles.css) y aprovecha el ancho: pago a la izquierda,
 * promociones a la derecha.
 *
 * En movil el grid colapsa a una columna y el aside queda DESPUES del pago en el DOM, asi que la
 * publicidad aparece siempre por debajo del boton de pagar. Es una regla, no una casualidad del
 * diseno: nada puede empujar el boton fuera de la pantalla.
 *
 * El modo embebido no cambia: sigue con ROOT_CLASS, fondo transparente y una sola columna, porque
 * ahi la pagina es del comercio.
 */
/*
 * `min-h-[100dvh]` y no `min-h-full`: el contenedor no tiene un padre con altura definida en toda la
 * cadena, asi que un 100% se resuelve contra el contenido y el fondo se cortaba justo debajo de la
 * tarjeta, dejando una banda blanca en pantallas altas. `dvh` ademas sigue a la barra de direcciones
 * en movil, donde `vh` deja un hueco al hacer scroll.
 */
const PAGE_ROOT_CLASS =
  'apipay-page flex min-h-[100dvh] w-full flex-col items-center px-4 py-8 ' +
  'text-[var(--apipay-color-text,#0f172a)] sm:py-12';

const PAGE_INNER_CLASS = 'flex w-full max-w-[62rem] flex-col gap-4';

/*
 * PANEL TRASERO: la cabecera del hub y las dos columnas viven DENTRO del mismo objeto.
 *
 * Antes la cabecera flotaba suelta sobre el degradado, separada de las tarjetas por un hueco, y el
 * logo y el sello de «pago seguro» quedaban en los dos extremos sin nada que los relacionara con lo
 * de abajo: se leian como dos adornos de la pagina, no como la cabecera de un checkout.
 *
 * El panel es TRASLUCIDO y no blanco a proposito. Blanco solido dejaria las tarjetas --que tambien
 * son blancas-- planas sobre el, que es justo lo que `SURFACE_CLASS` documenta haber corregido. Asi
 * queda una jerarquia de tres capas: degradado de la pagina, panel, tarjetas.
 */
const PAGE_PANEL_CLASS =
  'flex flex-col gap-4 rounded-[calc(var(--apipay-radius,14px)+8px)] bg-white/50 p-4 ' +
  'ring-1 ring-white/70 shadow-[0_12px_32px_-20px_rgb(15_23_42_/_0.30)] ' +
  // `apipay-sin-anclaje` NO es estilo: el `backdrop-blur` de aqui al lado convierte a este panel en
  // bloque contenedor de los elementos `position: fixed` que cuelgan de el, y eso le robaba la
  // ventana al reto 3DS del banco. El porque completo, con las medidas, en styles.css.
  'backdrop-blur-[2px] apipay-sin-anclaje sm:p-5';

/**
 * La cabecera dentro del panel, como BANDA BLANCA que sangra hasta sus bordes.
 *
 * <p>Los margenes negativos cancelan el `p-4 sm:p-5` del panel y la padding se vuelve a poner aqui;
 * el radio superior copia el del panel para que la banda encaje en la esquina en vez de asomar.
 *
 * <p><strong>Y el blanco no es decorativo: es lo que permite que el logo no lleve marco.</strong>
 * El asset de ApiPay es opaco --comprobado el 2026-08-24: el PNG es tipo de color 2, RGB sin canal
 * alfa ni chunk `tRNS`, y el WebP es un `VP8 ` lossy sin `ALPH`--, asi que sobre el panel
 * translucido su fondo blanco se veria como un rectangulo recortado. Con la banda blanca detras, el
 * blanco del asset se funde y el logo puede ir suelto, sin pastilla ni sombra.
 */
const PAGE_HEADER_CLASS =
  '-mx-4 -mt-4 rounded-t-[calc(var(--apipay-radius,14px)+8px)] border-b border-black/[0.07] ' +
  'bg-white px-4 py-4 sm:-mx-5 sm:-mt-5 sm:px-5 sm:py-5';


/*
 * La segunda pista mide 20rem SIEMPRE, tambien cuando no hay publicidad que mostrar. Asi la columna
 * del pago no cambia de ancho al pasar de una pantalla a otra: un checkout que se reencuadra a mitad
 * del flujo se siente roto aunque funcione.
 *
 * DOS FILAS, y cada pieza dice donde va. En pantalla ancha el reparto es:
 *
 *     col 1                        col 2
 *   +---------------------------+ +------------------+
 *   | Elige como pagar          | | resumen + carrito|  fila 1
 *   |                           | +------------------+
 *   | (abarca las dos filas)    | | publicidad       |  fila 2
 *   +---------------------------+ +------------------+
 *
 * El `row-span-2` de la izquierda no es decorativo: sin el, la fila 1 crecería hasta la altura del
 * selector y la publicidad caería al fondo de la pagina en vez de quedar pegada bajo el resumen.
 *
 * Y el ORDEN DEL DOM es resumen -> selector -> publicidad, que es el que manda por debajo de `lg`,
 * donde todo se apila en una columna. Ahi lo primero tiene que ser QUE se paga; elegir medio de pago
 * antes de haber visto el importe es pedirle al comprador que decida a ciegas.
 */
/*
 * La rejilla y la columna del pago las decide `layoutPagina.ts`, que explica por que hay DOS
 * formas. Antes se declaraba siempre la de dos columnas y, en las pantallas de desenlace --sin
 * resumen ni promociones--, la pista vacia de 20rem se seguia reservando: la tarjeta quedaba
 * pegada a la izquierda de un panel de 62rem.
 */

/** Segundos antes de volver solo a la tienda. El porque del numero, en el caso `success`. */
const SEGUNDOS_VUELTA_AUTOMATICA = 8;

/* PAGE_COLUMN_CLASS se fue con las dos clases de columna, a layoutPagina.ts. */

/*
 * La columna del pago: primera columna, abarca las dos filas (ver PAGE_GRID_CLASS), y ADEMAS llega
 * hasta el fondo de la columna de al lado.
 *
 * Las dos piezas del final hacen eso ultimo, y las dos hacen falta:
 *
 *   * `lg:self-stretch` cancela el `items-start` de la rejilla SOLO para esta columna, asi que su
 *     caja pasa a medir las dos filas enteras --resumen + hueco + publicidad-- en vez de lo que
 *     ocupe su contenido;
 *   * `[&>*:last-child]:grow` hace que sea la ULTIMA tarjeta la que se coma esa diferencia, porque
 *     estirar la caja sin estirar lo de dentro solo mueve el hueco de sitio.
 *
 * Por que crece esta columna y no la publicidad: el carrusel fija su alto con `aspect-[16/10]`
 * (PromoCarousel), asi que estirarlo deformaria la ilustracion. Y por que no se toca el resumen:
 * `items-start` lo deja a su altura natural, que es lo correcto --cuando no hay publicidad, la fila
 * 2 esta vacia y un resumen estirado seria una tarjeta enorme medio vacia--.
 */
/*
 * La columna del pago: primera columna, abarca las dos filas (ver PAGE_GRID_CLASS), y llega hasta el
 * fondo de la columna de al lado.
 *
 * Tres piezas, y las tres hacen falta:
 *
 *   * `lg:self-stretch` cancela el `items-start` de la rejilla SOLO para esta columna, asi que su
 *     caja pasa a medir las dos filas enteras --resumen + hueco + publicidad-- en vez de lo que
 *     ocupe su contenido;
 *   * `[&>*:last-child]:grow` hace que sea la ULTIMA tarjeta la que se coma esa diferencia, porque
 *     estirar la caja sin estirar lo de dentro solo mueve el hueco de sitio;
 *   * y dentro de esa tarjeta, la lista de medios de pago lleva `grow` (MethodSelector), que es lo
 *     que manda el sobrante ARRIBA del boton en vez de dejarlo debajo. Sin esa tercera pieza un
 *     carrito largo abria doscientos px de vacio al final de la tarjeta.
 *
 * Por que crece esta columna y no la publicidad: el carrusel fija su alto con `aspect-[16/10]`
 * (PromoCarousel), asi que estirarlo deformaria la ilustracion. Y por que no se toca el resumen:
 * `items-start` lo deja a su altura natural, que es lo correcto --cuando no hay publicidad, la fila
 * 2 esta vacia y un resumen estirado seria una tarjeta enorme medio vacia--.
 */
/* La columna del pago la da `claseDeColumnaPrincipal`, en layoutPagina.ts. */

/** El resumen del cobro: segunda columna, arriba. */
const PAGE_SUMMARY_CLASS = 'min-w-0 lg:col-start-2 lg:row-start-1';

/** La publicidad: segunda columna, debajo del resumen. */
const PAGE_PROMOS_CLASS = 'min-w-0 lg:col-start-2 lg:row-start-2';

/**
 * Que cuenta como PANTALLA DISTINTA a efectos de remontar el cuerpo.
 *
 * <p>El envoltorio del cuerpo lleva esta clave, y una clave nueva significa para React «tira lo que
 * habia y monta otra cosa». Eso es justo lo que se quiere entre pantallas de verdad distintas —es
 * lo que hace que la animacion de entrada vuelva a correr— y justo lo que NO se puede hacer
 * mientras un flujo de pasarela tiene el control.
 *
 * <p>Por eso `requires_action` y `gateway_flow` comparten clave: <strong>son la misma pantalla</strong>,
 * las dos pintan el mismo `GatewayFlow` (el `default` de `renderBody`). Lo unico que cambia entre
 * ellas es que el intent ya trae a donde ir. Darles claves distintas remontaba la estrategia en el
 * instante exacto en que el primer confirm respondia, y con ella morian sus `useRef` —un ref no
 * sobrevive a un remontaje—: el que recordaba «pedi la URL de redireccion, salta en cuanto llegue»
 * volvia a `false` y el salto no ocurria nunca.
 *
 * <p>Se midio empotrado, que es donde no hay auto-arranque que lo tape: el comprador pulsaba «Pagar
 * con Webpay Plus», el widget pedia la direccion, la recibia… y no pasaba nada. Hacia falta un
 * SEGUNDO click, que entonces si saltaba porque la URL ya estaba en el intent.
 */
function claveDePantalla(uiState: UiState): UiState {
  return uiState === 'requires_action' ? 'gateway_flow' : uiState;
}

/**
 * Proyecta el intent a la pasarela elegida por el comprador, para que `GatewayFlow`
 * siga despachando por `checkout.gateway_id` sin recibir props extra.
 */
function withSelectedGateway(intent: PaymentIntent, gatewayId: string): PaymentIntent {
  if (gatewayId === intent.checkout.gateway_id) {
    return intent;
  }
  const method = intent.checkout.payment_methods?.find(
    (candidate) => candidate.gateway_id === gatewayId,
  );
  const config = method?.public_config ?? {};
  const checkout: PaymentIntentCheckout = {
    ...intent.checkout,
    gateway_id: gatewayId,
    gateway_public_key: publicKeyFromConfig(config),
    gateway_session_id: sessionIdFromConfig(config),
  };
  return { ...intent, checkout };
}

/**
 * Pasarela que el comprador habia elegido antes de salir hacia el formulario de una pasarela.
 *
 * <p>Al volver de una inscripcion de tarjeta el widget arranca DE CERO: la eleccion del selector se
 * perdio con la navegacion, y sin recuperarla el despachador montaria la estrategia de otra pasarela
 * —la primera que traiga la sesion— y la inscripcion abierta quedaria sin cerrar con el dinero
 * pendiente de cobro. El `ins_...` recordado guarda tambien con QUE se estaba pagando, justo para
 * esto.
 *
 * <p>Solo en modo pagina: la memoria de la sesion alojada no se escribe embebido (ver hostedSession).
 * Y solo para el intent de esta sesion, que se deriva del `client_secret` sin esperar a la API.
 */
function recalledGatewayId(clientSecret: string, isTopLevel: boolean): string | null {
  if (!isTopLevel) {
    return null;
  }
  const pending = recallPendingInscription(intentIdFromClientSecret(clientSecret));
  return pending === null ? null : pending.gatewayId;
}

export function SessionPage(props: SessionPageProps): JSX.Element {
  const { clientSecret, publicKey, nonce, locale, apiBaseUrl, frameContext, messageTarget } = props;

  const context = useMemo<FrameContext>(
    () => frameContext ?? currentFrameContext(),
    [frameContext],
  );

  const session = useCheckoutSession({ clientSecret, publicKey, apiBaseUrl });
  const { intent, problem } = session;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const readyRef = useRef(false);
  const settledRef = useRef(false);

  const [blocked, setBlocked] = useState(false);
  const [canceled, setCanceled] = useState(false);
  // Se resuelve en el inicializador y no en un efecto: hacerlo despues del primer pintado ensenaria
  // el selector un instante para reemplazarlo acto seguido, en una pantalla donde el comprador ya
  // eligio.
  const [selectedGatewayId, setSelectedGatewayId] = useState<string | null>(() =>
    recalledGatewayId(clientSecret, context.isTopLevel),
  );

  // Abierto en una ventana por el comercio: al terminar, se le avisa y la ventana se cierra sola.
  useAvisoYCierreDeVentana({ intent, canceladoAqui: canceled, esVentanaPrincipal: context.isTopLevel });

  /** El intent de esta sesion, derivado del `client_secret` y disponible aunque la API no responda. */
  const intentId = useMemo(() => intentIdFromClientSecret(clientSecret), [clientSecret]);

  /*
   * La vuelta a la tienda que se recordo en una carga anterior de esta pestana.
   *
   * Se lee UNA VEZ, al montar, y solo en modo pagina --embebido la memoria de la sesion alojada ni
   * se escribe ni hace falta, ver hostedSession--. Es lo unico que le queda a la pantalla de error
   * cuando el GET del intent no llega, porque `merchant_return_url` vive dentro de ese intent.
   */
  const [rememberedReturnUrl] = useState<string | null>(() =>
    context.isTopLevel ? recallMerchantReturnUrl(intentIdFromClientSecret(clientSecret)) : null,
  );

  /*
   * Y se recuerda en cuanto una carga SI funciona, para la siguiente que no: la vuelta de la
   * pasarela es una carga nueva, y es justo donde un fallo deja al comprador encerrado despues de
   * haber pagado.
   */
  const merchantReturnUrl = intent?.merchant_return_url;
  useEffect(() => {
    if (!context.isTopLevel || intentId === null || merchantReturnUrl === undefined) {
      return;
    }
    rememberMerchantReturnUrl(intentId, merchantReturnUrl);
  }, [context.isTopLevel, intentId, merchantReturnUrl]);

  /**
   * El canal se abre hacia el origen embebedor DETECTADO, que es el unico destino
   * posible: quien nos embebe ya conoce el nonce porque el mismo construyo la URL
   * del iframe. La autorizacion real se comprueba abajo contra allowed_origins, y
   * mientras no este resuelta solo salen `ready` y `resize`, que no llevan datos
   * del pago.
   */
  const bridge = useMemo<WidgetBridge | null>(() => {
    // En modo pagina no hay canal, y cortarlo aqui NO es cosmetico: sin ventana padre
    // `detectEmbedderOrigin` cae al `document.referrer`, que en una navegacion normal es el sitio
    // desde el que vino el comprador. Sin esta guarda se abriria un canal postMessage hacia un
    // origen que no nos embebe y que nada tiene que ver con la sesion.
    if (context.isTopLevel) {
      return null;
    }
    const embedder = detectEmbedderOrigin(context);
    if (embedder === null) {
      return null;
    }
    try {
      return createBridge({ nonce, targetOrigin: embedder, target: messageTarget });
    } catch {
      return null;
    }
  }, [context, messageTarget, nonce]);

  // Handshake: el SDK desmonta el iframe si no recibe apipay:ready en 10 s.
  useEffect(() => {
    if (bridge === null || readyRef.current) {
      return;
    }
    readyRef.current = true;
    bridge.ready();
  }, [bridge]);

  const allowedOrigins = intent?.checkout.allowed_origins;

  useEffect(() => {
    if (allowedOrigins === undefined) {
      return;
    }
    if (context.isTopLevel) {
      // MODO PAGINA: el checkout ES el documento, no va embebido en el del comercio.
      //
      // `allowed_origins` no aplica aqui, y no por relajar la regla: lo que esa lista autoriza es
      // QUIEN puede embebernos, y sin embebedor no hay a quien autorizar ni a quien dirigir el
      // canal postMessage —`bridge` ya vale null en este caso—. El riesgo que cubre el guardia es
      // que un sitio ajeno nos meta en un iframe para robar la interaccion; cuando somos el
      // documento superior, ese riesgo no existe. La sesion la sigue protegiendo el par
      // `pk_` + `client_secret`, que es lo que de verdad da acceso al intent.
      //
      // Este modo es el que necesitan las pasarelas de REDIRECCION (Webpay Plus, MercadoPago
      // Checkout Pro): el comprador se va al sitio de la pasarela y vuelve, y meter eso en un
      // iframe solo anade fricción. El iframe sigue siendo obligatorio para las pasarelas con
      // campos alojados, donde es la frontera de PCI DSS SAQ A.
      setBlocked(false);
      return;
    }
    try {
      assertEmbeddedFromAllowedOrigin(allowedOrigins, context);
      setBlocked(false);
    } catch {
      setBlocked(true);
    }
  }, [allowedOrigins, context]);

  const theme = intent?.checkout.theme;

  useEffect(() => {
    applyTenantTheme(theme);
  }, [theme]);

  // Altura del contenido -> apipay:resize, con debounce por requestAnimationFrame
  // para no inundar el canal durante animaciones o cambios de layout encadenados.
  useEffect(() => {
    const node = rootRef.current;
    if (node === null || bridge === null) {
      return;
    }

    const measure = (): number =>
      Math.min(Math.ceil(node.getBoundingClientRect().height), MAX_HEIGHT_PX);

    if (typeof ResizeObserver !== 'function') {
      bridge.resize(measure());
      return;
    }

    let frame = 0;
    const emit = (): void => {
      frame = 0;
      bridge.resize(measure());
    };
    const observer = new ResizeObserver(() => {
      if (frame !== 0) {
        return;
      }
      frame = requestAnimationFrame(emit);
    });
    observer.observe(node);
    emit();

    return (): void => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      observer.disconnect();
    };
  }, [bridge]);

  // Desenlace terminal: exactamente uno, y una sola vez.
  useEffect(() => {
    if (bridge === null || settledRef.current) {
      return;
    }

    if (blocked) {
      settledRef.current = true;
      bridge.error('embed_origin_denied', t(locale, 'error.embed_origin_denied'));
      return;
    }

    if (canceled) {
      settledRef.current = true;
      bridge.cancel();
      return;
    }

    if (intent !== null) {
      switch (intent.status) {
        case 'SUCCEEDED':
          settledRef.current = true;
          bridge.success(intent.id);
          return;
        case 'FAILED':
          settledRef.current = true;
          // El code es SIEMPRE `payment_failed` (plan 7.3.3): el detalle de la
          // pasarela viaja en el mensaje, no en el codigo estable del protocolo.
          bridge.error(
            'payment_failed',
            intent.last_error?.message ?? t(locale, 'status.failed.body'),
          );
          return;
        case 'EXPIRED':
          settledRef.current = true;
          bridge.error('intent_expired', t(locale, 'status.expired.body'));
          return;
        case 'CANCELED':
          settledRef.current = true;
          bridge.cancel();
          return;
        /*
         * Una venta que se cobro y el comercio anulo DESPUES. El desenlace se emite igual --si no,
         * la pagina del comercio que monta el widget empotrado se queda esperando un evento que no
         * llega nunca-- y es un `error`, no un `cancel`: `cancel` significa «el comprador se fue
         * sin pagar», y aqui pago. El codigo es propio para que el comercio pueda distinguirlo.
         *
         * No sustituye al webhook `payment_intent.reversed`, que es el canal por el que el comercio
         * se entera de verdad: esto solo cuenta lo que ve ESTA sesion reabierta.
         */
        case 'REVERSED':
          settledRef.current = true;
          bridge.error('payment_reversed', t(locale, 'status.reversed.body'));
          return;
        default:
          return;
      }
    }

    // Sin intent y con problema: la sesion no arranco (client_secret invalido,
    // intent inexistente, red caida). Se avisa al comercio con el code estable.
    if (problem !== null) {
      settledRef.current = true;
      bridge.error(problem.code, t(locale, problemMessageKey(problem.code)));
    }
  }, [blocked, bridge, canceled, intent, locale, problem]);

  const handleSelect = useCallback((gatewayId: string): void => {
    setSelectedGatewayId(gatewayId);
  }, []);

  const handleCancel = useCallback((): void => {
    setCanceled(true);
  }, []);

  const activeIntent = useMemo<PaymentIntent | null>(() => {
    if (intent === null || selectedGatewayId === null) {
      return intent;
    }
    return withSelectedGateway(intent, selectedGatewayId);
  }, [intent, selectedGatewayId]);

  /**
   * La eleccion del comprador viaja en CADA confirm de esta sesion.
   *
   * <p>Se decora aqui y no en cada estrategia de pasarela porque la eleccion es de la sesion, no del
   * flujo: si el intent nacio sin pasarela —el caso de un hub—, el servidor la fija con lo que llegue
   * en el primer confirm, y el segundo (el commit del retorno) la repite sin efecto.
   */
  const activeSession = useMemo<CheckoutSession>(() => {
    if (selectedGatewayId === null) {
      return session;
    }
    return {
      ...session,
      confirm: (payload) => session.confirm({ ...payload, gateway_id: selectedGatewayId }),
    };
  }, [session, selectedGatewayId]);

  const uiState = useMemo<UiState>(() => {
    if (blocked) {
      return 'blocked';
    }
    if (canceled) {
      return 'failed';
    }
    if (session.uiState === 'select_method' && selectedGatewayId !== null) {
      return 'gateway_flow';
    }
    return session.uiState;
  }, [blocked, canceled, selectedGatewayId, session.uiState]);

  /*
   * Los estados que pintan la tarjeta de resumen. Las pantallas terminales no la pintan: son un
   * StatusScreen a secas.
   *
   * Hasta el 2026-08-24 esto decidia ademas si la marca del hub se metia DENTRO de esa tarjeta. Ya
   * no: en modo pagina el resumen se fue a la columna derecha, y meter ahi la cabecera de la
   * plataforma la habria mandado al lado equivocado de la pantalla. Vuelve a ir suelta encima de
   * las dos columnas, que es como se pintaba ya en los estados terminales.
   */
  const hasSummaryCard = pintaResumen(uiState, activeIntent !== null);

  /** Ver `claveDePantalla`: NO es `uiState` a secas, y esa diferencia sujeta el salto a la pasarela. */
  const claveDeLaPantalla = claveDePantalla(uiState);

  /*
   * El logo pesa distinto segun el modo, y no es un detalle estetico.
   *
   * EMBEBIDO: la pagina es del COMERCIO y el widget vive en un iframe pequeno. ApiPay es aqui una
   * marca de confianza, no la protagonista: se queda en 104px.
   *
   * PAGINA: el documento ES de ApiPay —dominio propio, pantalla completa, el comprador ha salido del
   * sitio del comercio— asi que la marca de la plataforma manda en la cabecera. El asset es de 480x134
   * y se venia sirviendo al 22%: un wordmark gris a ese tamano sobre fondo claro se leia como marca de
   * agua.
   *
   * Desde el 2026-08-24 va a 208px (mismo ratio 3.58). Antes eran 168, y el techo lo ponia la
   * pastilla: un logo mas grande dentro de un marco flotante se volvia un cartel. Ahora la cabecera
   * es una banda blanca del panel (PAGE_HEADER_CLASS), el logo va suelto sobre ella, y puede pesar
   * lo que le corresponde al dueño de la pagina sin competir con nada.
   *
   * No se recolorea el logo: el gris del wordmark y el degradado de la linea son la marca.
   */
  /*
   * Desde 2026-10 el asset es el wordmark de la marca nueva (isotipo + APIPAY), recortado sin aire:
   * ratio 5.29, no 3.58. Por eso baja el ALTO y no el ancho: a 58px de alto mediria 307px y se
   * comeria la cabecera. A 40px mide 212, practicamente lo que ocupaba el anterior.
   */
  const logoWidth = context.isTopLevel ? 212 : 116;
  const logoHeight = context.isTopLevel ? 40 : 22;

  /*
   * Marca del hub, presente en TODA la sesion y no solo en el selector: el comprador llega aqui
   * desde el sitio del comercio y necesita saber en todo momento quien procesa el cobro. El logo
   * del COMERCIO es otra cosa y lo pinta PaymentSummary. El webp pesa 4 KB; el png es la reserva.
   *
   * Se extrae a una constante porque lo comparten los dos modos de render (pagina y embebido).
   */
  const header = (
    <header className="flex items-center justify-between gap-3">
      {/*
        SIN pastilla, y sin sombra. Hasta el 2026-08-24 el logo iba enmarcado en modo pagina porque
        flotaba sobre el fondo degradado y su blanco opaco se leia como un recorte. Ya no flota: vive
        sobre la banda blanca de la cabecera del panel, donde ese mismo blanco es invisible.

        Elevar algo que esta DENTRO de un contenedor es decirle al ojo que esta por encima de el, y
        no lo esta. El marco era la solucion a un problema que se resolvio de otra forma.
      */}
      <span className="inline-flex items-center">
        <picture>
          <source srcSet="/apipay-logo.webp" type="image/webp" />
          <img
            src="/apipay-logo.png"
            alt="ApiPay"
            width={logoWidth}
            height={logoHeight}
            decoding="async"
            style={{ height: `${String(logoHeight)}px` }}
            className="w-auto"
          />
        </picture>
      </span>
      <span
        className={
          context.isTopLevel
            ? 'inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-xs ' +
              'font-medium text-[var(--apipay-color-muted,#64748b)] ring-1 ring-black/[0.06] ' +
              // Hoy este sello NO es ancestro de ninguna pasarela, pero todo `backdrop-blur` del
              // widget lleva la marca: la regla es «desenfoque = posible anclaje», y asi el dia que
              // algo fijo cuelgue de aqui no hay que volver a descubrirlo. Ver styles.css.
              'backdrop-blur-sm apipay-sin-anclaje'
            : 'inline-flex items-center gap-1.5 text-xs text-[var(--apipay-color-muted,#64748b)]'
        }
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 7V5a4 4 0 1 1 8 0v2m-9 0h10v7H3V7Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
        {t(locale, 'common.secure_payment')}
      </span>
    </header>
  );

  const footer = (
    <p className="text-center text-xs text-[var(--apipay-color-muted,#64748b)]">
      {t(locale, 'common.powered_by')}
    </p>

  );

  const body = renderBody({
    uiState,
    canceled,
    locale,
    intent: activeIntent,
    session: activeSession,
    problemCode: problem?.code ?? null,
    problemDetail: problem?.detail ?? null,
    onSelect: handleSelect,
    onCancel: handleCancel,
    embedded: !context.isTopLevel,
    rememberedReturnUrl,
    autoStart: context.isTopLevel,
    withSummary: !context.isTopLevel,
  });

  if (context.isTopLevel) {
    /*
     * Las promociones solo acompanan a la ELECCION del medio de pago, que es donde el comprador
     * tiene un momento de espera propio. No aparecen mientras se procesa el cobro ni en la pantalla
     * del desenlace: competir con un "pago realizado" o con un rechazo esta fuera de lugar, y en el
     * tramo de la pasarela el comprador ya no esta aqui.
     */
    const promos = uiState === 'select_method' ? bundledPromoSource.promos() : [];

    return (
      <div
        ref={rootRef}
        data-testid="checkout-root"
        data-ui-state={uiState}
        data-mode="page"
        className={PAGE_ROOT_CLASS}
      >
        <div className={PAGE_INNER_CLASS}>
          <div className={PAGE_PANEL_CLASS}>
            <div className={PAGE_HEADER_CLASS}>{header}</div>
            <div className={claseDeRejilla(hasSummaryCard || promos.length > 0)}>
              {/*
                El resumen va PRIMERO en el DOM aunque se pinte a la derecha: es lo que decide el
                orden al apilarse en movil. Ver PAGE_GRID_CLASS.
              */}
              {!hasSummaryCard || activeIntent === null ? null : (
                <div className={PAGE_SUMMARY_CLASS}>
                  <PaymentSummary intent={activeIntent} locale={locale} />
                </div>
              )}
              {/*
                `key` y `data-pantalla` con el mismo valor, y no es redundante: la clave hace que
                React REMONTE el nodo al cambiar de pantalla, que es lo unico que hace que la
                animacion de entrada vuelva a correr --una animacion CSS solo corre al montar--.
                El atributo deja el estado comprobable desde una prueba.

                NO BORRAR LA CLAVE pensando que sobra porque el atributo ya la repite: sin ella
                la entrada se ve una vez y nunca mas, y ningun test lo caza hoy.

                El valor lo da `claveDePantalla`, y no `uiState` a secas: ver alli por que.
              */}
              <div
                key={claveDeLaPantalla}
                data-pantalla={claveDeLaPantalla}
                className={`${claseDeColumnaPrincipal(hasSummaryCard || promos.length > 0)} apipay-entra apipay-sin-anclaje`}
              >
                {body}
              </div>
              {promos.length === 0 ? null : (
                <aside className={PAGE_PROMOS_CLASS} data-testid="checkout-promos">
                  <PromoCarousel promos={promos} label={t(locale, 'promo.label')} />
                </aside>
              )}
            </div>
          </div>
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid="checkout-root"
      data-ui-state={uiState}
      data-mode="embedded"
      className={ROOT_CLASS}
    >
      {header}
      <div
        key={claveDeLaPantalla}
        data-pantalla={claveDeLaPantalla}
        className="apipay-entra apipay-sin-anclaje"
      >
        {body}
      </div>
      {footer}
    </div>
  );
}

interface BodyArgs {
  uiState: UiState;
  canceled: boolean;
  locale: Locale;
  intent: PaymentIntent | null;
  session: CheckoutSession;
  problemCode: string | null;
  /**
   * `detail` del problem+json que tumbo la CARGA de la sesion. Viaja aparte del codigo porque es lo
   * que dice al comercio por que su intent no se pudo abrir; la copia por `code` no lo sabe.
   */
  problemDetail: string | null;
  onSelect: (gatewayId: string) => void;
  onCancel: () => void;
  /** Si el widget vive dentro de un iframe del comercio; decide como es la vuelta a la tienda. */
  embedded: boolean;
  /**
   * Vuelta a la tienda recordada de una carga anterior, para la pantalla en la que el intent NO
   * esta. Es null en cuanto hay intent: alli manda su `merchant_return_url`, que es el de esta carga.
   */
  rememberedReturnUrl: string | null;
  /** Modo pagina: el flujo arranca sin pedir un segundo click (ver GatewayFlowProps.autoStart). */
  autoStart: boolean;
  /**
   * Si el cuerpo pinta la tarjeta de resumen.
   *
   * <p>Embebido si: el widget es una sola columna dentro de la pagina del comercio y el resumen
   * encabeza el flujo. En modo pagina NO, porque alli lo pinta SessionPage en la columna derecha,
   * encima de la publicidad; dejarlo tambien aqui daria dos resumenes del mismo cobro.
   */
  withSummary: boolean;
}

function renderBody(args: BodyArgs): JSX.Element {
  const {
    uiState,
    canceled,
    locale,
    intent,
    session,
    problemCode,
    problemDetail,
    onSelect,
    onCancel,
    embedded,
    rememberedReturnUrl,
    autoStart,
    withSummary,
  } = args;

  if (uiState === 'blocked') {
    // Estado bloqueado: pantalla neutra, sin un solo dato del intent.
    return <StatusScreen status="blocked" locale={locale} />;
  }

  /*
   * SE MIRA TAMBIEN EL ESTADO DEL INTENT, y no solo la bandera local.
   *
   * La bandera dice «el comprador se fue en ESTA pestana». Desde el 2026-09-29 hay un segundo
   * camino a la misma pantalla y no pasa por ella: el comprador anula en el formulario de
   * Transbank, el widget se lo reporta al servidor y es el SERVIDOR el que deja el intent en
   * CANCELED. Ese estado llega en la respuesta del confirm y en cualquier carga posterior de la
   * sesion, donde la bandera ya no existe.
   *
   * Sin esta segunda condicion, quien recargara la vuelta leia «No pudimos procesar tu pago» sobre
   * un cobro que nadie rompio: `mapIntentToUiState` manda CANCELED a `failed` y la rama de abajo
   * pinta el titulo del rechazo. Es el mismo texto contradictorio que se corrigio el 2026-09-23
   * entrando por el otro camino.
   */
  if (canceled || intent?.status === 'CANCELED') {
    return (
      /*
       * EL ESTADO SIGUE SIENDO `failed` --CANCELED comparte pantalla con FAILED en
       * `mapIntentToUiState`, y eso es lo que el resto del widget observa por `data-status`-- PERO
       * EL TEXTO ES EL DE LA CANCELACION, LOS DOS.
       *
       * Hasta el 2026-09-23 solo se sustituia el cuerpo, asi que la pantalla se contradecia: de
       * titulo «No pudimos procesar tu pago» y de cuerpo «Cancelaste el pago». Una nos echa la culpa
       * y la otra dice que fue el comprador. La causa estaba medida: existia `status.canceled.body`
       * y no existia `status.canceled.title`, con lo que el titulo caia al del rechazo.
       *
       * No es cosmetico: quien lee esa pantalla decide si escribe a soporte por un cobro que nadie
       * ha roto.
       */
      <StatusScreen
        status="failed"
        locale={locale}
        title={t(locale, 'status.canceled.title')}
        message={t(locale, 'status.canceled.body')}
        code="payment_canceled"
        merchantReturnUrl={intent?.merchant_return_url}
        reference={intent?.id}
      />
    );
  }

  if (intent === null) {
    if (problemCode !== null) {
      // La sesion no se pudo cargar. Sin intent no hay selector ni pantalla terminal, asi que esta
      // es la unica superficie que puede darle una salida al comprador: ver SessionErrorScreen.
      return (
        <SessionErrorScreen
          code={problemCode}
          message={t(locale, problemMessageKey(problemCode))}
          detail={problemDetail}
          locale={locale}
          onRetry={session.startPolling}
          merchantReturnUrl={rememberedReturnUrl ?? undefined}
          embedded={embedded}
        />
      );
    }
    // Misma pantalla que el resto de la espera, y conserva el testid con el que ya se la
    // conocia. Cuando el comercio fija la pasarela, este es el PRIMER fotograma de un traspaso
    // que sigue con el chunk de la estrategia y acaba en la pasarela: ver HandoffScreen.
    return <HandoffScreen message={t(locale, 'loading.session')} testId="loading-indicator" />;
  }

  switch (uiState) {
    case 'success':
      return (
        <StatusScreen
          status="success"
          locale={locale}
          merchantReturnUrl={intent?.merchant_return_url}
          /*
           * OCHO segundos, y el numero no es arbitrario:
           *
           *   - por debajo de CINCO no da tiempo a leer las tres cosas que esta pantalla existe
           *     para ensenar --que el pago se hizo, cuanto, y la referencia--, y el comprador se
           *     va con la sensacion de no haber visto el comprobante;
           *   - por encima de DOCE la pantalla se siente colgada y la gente pulsa igual.
           *
           * Solo en el desenlace bueno: en un rechazo o en un expirado el comprador necesita leer
           * el motivo y decidir si reintenta, y llevarselo de ahi tira por tierra el reintento que
           * esa pantalla ofrece. Y solo en modo pagina: empotrado, la pagina de alrededor ya es
           * del comercio y quien decide como salir es el.
           */
          {...(embedded || intent.merchant_return_url === undefined
            ? {}
            : {
                autoReturnSeconds: SEGUNDOS_VUELTA_AUTOMATICA,
                onAutoReturn: (): void => {
                  navegarALaTienda(intent.merchant_return_url as string);
                },
              })}
          // El importe solo viaja al desenlace bueno; StatusScreen documenta por que no se ensena
          // en un rechazo.
          amount={formatAmount(intent.amount_minor, intent.currency, locale)}
          reference={intent.id}
        />
      );
    case 'expired':
      return (
        <StatusScreen
          status="expired"
          locale={locale}
          merchantReturnUrl={intent?.merchant_return_url}
          reference={intent.id}
        />
      );
    case 'reversed':
      return (
        <StatusScreen
          status="reversed"
          locale={locale}
          merchantReturnUrl={intent.merchant_return_url}
          // La referencia SI, el importe NO: es la misma regla que el rechazo. Un importe grande
          // junto a «este pago fue anulado» se lee como «me acaban de cobrar esto», que es lo
          // contrario de lo que paso; la referencia, en cambio, es lo unico con lo que el
          // comprador puede preguntar por su dinero.
          reference={intent.id}
        />
      );
    case 'failed':
      return (
        <StatusScreen
          status="failed"
          locale={locale}
          message={intent.last_error?.message ?? t(locale, 'status.failed.body')}
          // El codigo queda legible en la pantalla terminal: aqui el GatewayFlow ya se desmonto y
          // con el su ErrorMessage, que era la otra superficie que publicaba `data-code`.
          code={intent.last_error?.code ?? 'payment_failed'}
          merchantReturnUrl={intent.merchant_return_url}
          reference={intent.id}
        />
      );
    case 'select_method':
      return (
        <>
          {withSummary ? <PaymentSummary intent={intent} locale={locale} /> : null}
          <MethodSelector
            intent={intent}
            locale={locale}
            onSelect={onSelect}
            merchantReturnUrl={intent.merchant_return_url}
            onReturnToMerchant={onCancel}
            embedded={embedded}
          />
        </>
      );
    case 'processing':
      return (
        <>
          {withSummary ? <PaymentSummary intent={intent} locale={locale} /> : null}
          <ProcessingScreen locale={locale} />
        </>
      );
    default:
      // 'gateway_flow', 'requires_action' y el residual 'loading' con intent cargado.
      return (
        <>
          {withSummary ? <PaymentSummary intent={intent} locale={locale} /> : null}
          <GatewayFlow
            intent={intent}
            session={session}
            locale={locale}
            onCancel={onCancel}
            autoStart={autoStart}
          />
        </>
      );
  }
}
